import React, { Component } from 'react';
import { getComplianceReport } from '../../../services/timeTrackingService';
import { isAllLocations, getLocationName, getLocationBadgeColor } from '../../../util/locationUtils';

/**
 * ComplianceReport - Display self vs manager check-in rates
 *
 * Props:
 * - locationId: number - installer location ID
 * - dateRange: { startDate, endDate }
 */
class ComplianceReport extends Component {
  state = {
    summary: {
      total_sessions: 0,
      self_checkin_rate: 0,
      clean_rate: 0,
      total_hours: '0.00'
    },
    byTechnician: [],
    loading: true,
    error: null
  };

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.locationId !== this.props.locationId ||
      prevProps.dateRange.startDate !== this.props.dateRange.startDate ||
      prevProps.dateRange.endDate !== this.props.dateRange.endDate
    ) {
      this.loadData();
    }
  }

  loadData = async () => {
    const { locationId, locations, dateRange } = this.props;

    if (!locationId) return;

    const isAggregate = isAllLocations(locationId) && Array.isArray(locations) && locations.length > 0;

    try {
      this.setState({ loading: true, error: null });

      let summary, byTechnician;

      if (isAggregate) {
        const results = await Promise.all(
          locations.map(loc =>
            getComplianceReport(loc.location_id, {
              startDate: dateRange.startDate,
              endDate: dateRange.endDate
            }).catch(() => ({ summary: {}, by_technician: [] }))
          )
        );

        // Merge by_technician: group by technician_id, sum counts, recalculate rates
        const techMap = {};
        results.forEach((result, i) => {
          const techs = result.by_technician || [];
          const loc = locations[i];
          techs.forEach(t => {
            const key = t.technician_id;
            if (!techMap[key]) {
              techMap[key] = {
                technician_id: t.technician_id,
                technician_name: t.technician_name,
                total_sessions: 0,
                self_checkins: 0,
                manager_checkins: 0,
                flagged_count: 0,
                auto_closed_count: 0,
                total_hours: 0,
                _locations: new Set()
              };
            }
            techMap[key].total_sessions += (t.total_sessions || 0);
            techMap[key].self_checkins += (t.self_checkins || 0);
            techMap[key].manager_checkins += (t.manager_checkins || 0);
            techMap[key].flagged_count += (t.flagged_count || 0);
            techMap[key].auto_closed_count += (t.auto_closed_count || 0);
            techMap[key].total_hours += parseFloat(t.total_hours || 0);
            techMap[key]._locations.add(getLocationName(loc));
          });
        });

        byTechnician = Object.values(techMap).map(t => {
          const selfRate = t.total_sessions > 0 ? (t.self_checkins / t.total_sessions) * 100 : 0;
          const cleanSessions = t.total_sessions - t.flagged_count - t.auto_closed_count;
          const cleanRate = t.total_sessions > 0 ? (cleanSessions / t.total_sessions) * 100 : 0;
          return {
            ...t,
            self_checkin_rate: selfRate,
            clean_rate: cleanRate,
            location_name: [...t._locations].join(', '),
            _locations: undefined
          };
        });

        // Recalculate summary from merged technician data
        const totalSessions = byTechnician.reduce((sum, t) => sum + t.total_sessions, 0);
        const totalSelfCheckins = byTechnician.reduce((sum, t) => sum + t.self_checkins, 0);
        const totalFlagged = byTechnician.reduce((sum, t) => sum + t.flagged_count, 0);
        const totalAutoClosed = byTechnician.reduce((sum, t) => sum + t.auto_closed_count, 0);
        const totalHours = byTechnician.reduce((sum, t) => sum + t.total_hours, 0);
        const cleanSessions = totalSessions - totalFlagged - totalAutoClosed;

        summary = {
          total_sessions: totalSessions,
          self_checkin_rate: totalSessions > 0 ? (totalSelfCheckins / totalSessions) * 100 : 0,
          clean_rate: totalSessions > 0 ? (cleanSessions / totalSessions) * 100 : 0,
          total_hours: totalHours
        };
      } else {
        const result = await getComplianceReport(locationId, {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        });
        summary = result.summary;
        byTechnician = result.by_technician || [];
      }

      this.setState({
        summary: summary || {
          total_sessions: 0,
          self_checkin_rate: 0,
          clean_rate: 0,
          total_hours: '0.00'
        },
        byTechnician,
        loading: false
      });
    } catch (error) {
      console.error('Error loading compliance report:', error);
      this.setState({
        error: 'Failed to load compliance data',
        loading: false
      });
    }
  };

  // Format percentage with 1 decimal
  formatPercent = (value) => {
    if (value === null || value === undefined) return '0.0%';
    return `${parseFloat(value).toFixed(1)}%`;
  };

  // Format hours with 2 decimals
  formatHours = (hours) => {
    if (hours === null || hours === undefined) return '0.00';
    const num = typeof hours === 'string' ? parseFloat(hours) : hours;
    return num.toFixed(2);
  };

  // Get color class based on percentage threshold
  getRateColorClass = (rate) => {
    if (rate >= 90) return 'text-success';
    if (rate >= 70) return 'text-warning';
    return 'text-danger';
  };

  // Get badge variant based on percentage threshold
  getRateBadgeVariant = (rate) => {
    if (rate >= 90) return 'success';
    if (rate >= 70) return 'warning';
    return 'danger';
  };

  // Render summary cards
  renderSummaryCards = () => {
    const { summary } = this.state;

    const selfRateClass = summary.self_checkin_rate >= 90 ? 'success' : summary.self_checkin_rate >= 70 ? 'warning' : 'danger';
    const cleanRateClass = summary.clean_rate >= 90 ? 'success' : summary.clean_rate >= 70 ? 'warning' : 'danger';

    return (
      <div className="overview-cards">
        <div className="overview-card">
          <div className="number">{summary.total_sessions || 0}</div>
          <div className="label">Total Sessions</div>
        </div>
        <div className={`overview-card ${selfRateClass}`}>
          <div className="number">{this.formatPercent(summary.self_checkin_rate)}</div>
          <div className="label">Self Check-in Rate</div>
        </div>
        <div className={`overview-card ${cleanRateClass}`}>
          <div className="number">{this.formatPercent(summary.clean_rate)}</div>
          <div className="label">Clean Session Rate</div>
        </div>
        <div className="overview-card">
          <div className="number">{this.formatHours(summary.total_hours)}</div>
          <div className="label">Total Hours</div>
        </div>
      </div>
    );
  };

  render() {
    const { byTechnician, loading, error } = this.state;

    // Loading state
    if (loading) {
      return (
        <div className="text-center py-4">
          <div className="spinner-border text-secondary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="text-muted mt-2 mb-0">Loading compliance data...</p>
        </div>
      );
    }

    // Error state
    if (error) {
      return (
        <div className="text-danger p-3">
          <i className="fa-sharp fa-regular fa-exclamation-circle mr-2"></i>
          {error}
        </div>
      );
    }

    const { locationId, locations } = this.props;
    const showLocation = isAllLocations(locationId) && Array.isArray(locations) && locations.length > 1;

    // Empty state
    if (!byTechnician || byTechnician.length === 0) {
      return (
        <div className="text-center py-4" style={{ background: 'transparent' }}>
          <p className="text-muted mb-0">No compliance data for the selected period.</p>
        </div>
      );
    }

    return (
      <div>
        {/* Summary Cards */}
        {this.renderSummaryCards()}

        {/* Legend */}
        <div className="mb-3 text-muted small">
          <span className="mr-3">
            <span className="badge badge-success mr-1">&nbsp;</span> 90%+
          </span>
          <span className="mr-3">
            <span className="badge badge-warning mr-1">&nbsp;</span> 70-90%
          </span>
          <span>
            <span className="badge badge-danger mr-1">&nbsp;</span> &lt;70%
          </span>
        </div>

        {/* Technician Table */}
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead className="bg-white">
              <tr>
                <th>Technician</th>
                {showLocation && <th>Location</th>}
                <th className="text-right">Sessions</th>
                <th className="text-right">Self Check-ins</th>
                <th className="text-right">Manager Check-ins</th>
                <th className="text-center">Self Rate</th>
                <th className="text-center">Clean Rate</th>
                <th className="text-right">Flagged</th>
                <th className="text-right">Auto-Closed</th>
              </tr>
            </thead>
            <tbody>
              {byTechnician.map((tech) => (
                <tr key={tech.technician_id}>
                  <td>
                    <strong>{tech.technician_name}</strong>
                  </td>
                  {showLocation && (
                    <td>
                      <span className="badge" style={{ backgroundColor: getLocationBadgeColor(tech.location_id, this.props.locations), color: '#fff' }}>
                        {tech.location_name}
                      </span>
                    </td>
                  )}
                  <td className="text-right">{tech.total_sessions || 0}</td>
                  <td className="text-right">{tech.self_checkins || 0}</td>
                  <td className="text-right">
                    {(tech.manager_checkins || 0) > 0 ? (
                      <span className="text-info">{tech.manager_checkins}</span>
                    ) : (
                      '0'
                    )}
                  </td>
                  <td className="text-center">
                    <span className={`badge badge-${this.getRateBadgeVariant(tech.self_checkin_rate)}`}>
                      {this.formatPercent(tech.self_checkin_rate)}
                    </span>
                  </td>
                  <td className="text-center">
                    <span className={`badge badge-${this.getRateBadgeVariant(tech.clean_rate)}`}>
                      {this.formatPercent(tech.clean_rate)}
                    </span>
                  </td>
                  <td className="text-right">
                    {(tech.flagged_count || 0) > 0 ? (
                      <span className="text-warning">{tech.flagged_count}</span>
                    ) : (
                      '0'
                    )}
                  </td>
                  <td className="text-right">
                    {(tech.auto_closed_count || 0) > 0 ? (
                      <span className="text-danger">{tech.auto_closed_count}</span>
                    ) : (
                      '0'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
}

export default ComplianceReport;
